import { useCallback, useState } from 'react';
import { TimeLog } from '../types';

interface UseWorklogEditOptions {
  /** Callback to save worklog changes */
  onSave: (id: string, startedTime: string, timeSpentSeconds: number, labels?: string[]) => Promise<void>;
  /** Callback to delete worklog */
  onDelete: (id: string) => Promise<void>;
}

interface EditModalProps {
  isOpen: boolean;
  timeLog: TimeLog | null;
  onClose: () => void;
  onSave: (id: string, startedTime: string, timeSpentSeconds: number, labels?: string[]) => Promise<void>;
}

interface DeleteModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmButtonText: string;
  cancelButtonText: string;
  variant: 'danger';
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

interface UseWorklogEditReturn {
  /** Props to spread on EditTimeLogModal */
  editModalProps: EditModalProps;
  /** Props to spread on ConfirmModal for delete */
  deleteModalProps: DeleteModalProps;
  /** Open edit modal for a worklog */
  handleEdit: (timeLog: TimeLog) => void;
  /** Open delete confirmation for a worklog */
  handleDeleteClick: (id: string) => void;
}

/**
 * Hook for managing worklog edit and delete operations.
 * Provides state and handlers for EditTimeLogModal and ConfirmModal.
 *
 * @example
 * ```tsx
 * const { editModalProps, deleteModalProps, handleEdit, handleDeleteClick } = useWorklogEdit({
 *   onSave: async (id, startedTime, timeSpentSeconds) => {
 *     await apiClient.patch(`/workLogs/${id}`, { startedTime, timeSpentSeconds });
 *     await refresh();
 *   },
 *   onDelete: async (id) => {
 *     await apiClient.delete(`/workLogs/${id}`);
 *     await refresh();
 *   },
 * });
 *
 * // In JSX:
 * <EditTimeLogModal {...editModalProps} />
 * <ConfirmModal {...deleteModalProps} />
 * ```
 */
export const useWorklogEdit = ({ onSave, onDelete }: UseWorklogEditOptions): UseWorklogEditReturn => {
  const [editingTimeLog, setEditingTimeLog] = useState<TimeLog | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const handleEdit = useCallback((timeLog: TimeLog) => {
    setEditingTimeLog(timeLog);
    setIsEditModalOpen(true);
  }, []);

  const handleCloseEdit = useCallback(() => {
    setIsEditModalOpen(false);
    setEditingTimeLog(null);
  }, []);

  const handleSaveEdit = useCallback(
    async (id: string, startedTime: string, timeSpentSeconds: number, labels?: string[]) => {
      await onSave(id, startedTime, timeSpentSeconds, labels);
    },
    [onSave]
  );

  const handleDeleteClick = useCallback((id: string) => {
    setDeleteConfirmId(id);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (deleteConfirmId) {
      try {
        await onDelete(deleteConfirmId);
      } catch (error) {
        console.error('Failed to delete time log:', error);
      } finally {
        setDeleteConfirmId(null);
      }
    }
  }, [deleteConfirmId, onDelete]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmId(null);
  }, []);

  const editModalProps: EditModalProps = {
    isOpen: isEditModalOpen,
    timeLog: editingTimeLog,
    onClose: handleCloseEdit,
    onSave: handleSaveEdit,
  };

  const deleteModalProps: DeleteModalProps = {
    isOpen: deleteConfirmId !== null,
    title: 'Delete work log',
    message: 'Delete this work log? This cannot be undone.',
    confirmButtonText: 'Delete',
    cancelButtonText: 'Cancel',
    variant: 'danger',
    onConfirm: handleDeleteConfirm,
    onCancel: handleDeleteCancel,
  };

  return {
    editModalProps,
    deleteModalProps,
    handleEdit,
    handleDeleteClick,
  };
};
